import { Icon, Icons } from '@/components/icon';
import { ContactAvatar } from '@/components/ui/contact-avatar';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

type PipSessionContact = {
  id: string;
  name?: string;
  lastName?: string;
  phoneNumber?: string;
  jobTitle?: string;
  companyName?: string;
  dialingState: 'pending' | 'ringing' | 'connected' | 'no-answer' | 'voicemail';
  imageUrl?: string;
};

type PipSessionData = {
  isActive: boolean;
  contacts: PipSessionContact[];
  activeContactId?: string;
  completedCount: number;
  totalCount: number;
  minutesLeft: number;
};

type PipSessionPanelProps = {
  session: PipSessionData;
  onEndSession: () => void;
  windowWidth: number;
  /** Called when the contact list is expanded/collapsed. */
  onToggleList?: (expanded: boolean) => void;
};

// ─── Helpers ────────────────────────────────────────────────

const getDisplayName = (contact: PipSessionContact): string => {
  const parts = [contact.name, contact.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  return contact.phoneNumber || 'Unknown';
};

const getStatusLabel = (state: PipSessionContact['dialingState']): string => {
  switch (state) {
    case 'connected':
      return 'Call answered';
    case 'voicemail':
      return 'Sent to voicemail';
    case 'no-answer':
      return 'No answer';
    case 'ringing':
      return 'Ringing…';
    default:
      return '';
  }
};

const getStatusIcon = (
  state: PipSessionContact['dialingState'],
): { icon: Icons; color: string } | null => {
  switch (state) {
    case 'connected':
      return { icon: Icons.Check, color: 'text-extra-highlight' };
    case 'voicemail':
      return { icon: Icons.Voicemail, color: 'text-status-danger' };
    case 'no-answer':
      return { icon: Icons.PhoneMissedCall, color: 'text-status-danger' };
    default:
      return null;
  }
};

// ─── Contact row ────────────────────────────────────────────

type PipContactRowProps = {
  contact: PipSessionContact;
  isActive: boolean;
  dimmed?: boolean;
  trailing?: React.ReactNode;
  showTags: boolean;
};

const PipContactRow = ({ contact, isActive, dimmed, trailing, showTags }: PipContactRowProps) => {
  const displayName = getDisplayName(contact);
  const avatarName =
    contact.name && contact.lastName
      ? `${contact.name} ${contact.lastName}`
      : contact.name || undefined;

  const jobTitle = contact.jobTitle || (contact.name ? 'no job title' : undefined);
  const companyName = contact.companyName || (contact.name ? 'no company' : undefined);
  const hasSubtitle = !!jobTitle || !!companyName;

  const isHistory = !['pending', 'ringing'].includes(contact.dialingState) && !isActive;
  const statusLabel = isHistory ? getStatusLabel(contact.dialingState) : undefined;
  const statusIcon = isHistory ? getStatusIcon(contact.dialingState) : null;

  return (
    <div
      className={cn(
        'flex min-h-[40px] flex-col items-start justify-center overflow-hidden rounded-[12px] px-1.5 py-2',
        isActive && 'border border-extra-highlight-medium bg-extra-highlight-light',
      )}
    >
      <div className="flex w-full items-center gap-2 px-1">
        <div className={cn('flex min-w-0 flex-1 flex-col gap-0.5', dimmed && 'opacity-50')}>
          <div className="flex items-center gap-1 px-1 py-0.5">
            <ContactAvatar name={avatarName} imageUrl={contact.imageUrl} size="sm" />
            <span className="truncate text-body-12 !font-medium text-primary-100">
              {displayName}
            </span>
          </div>
          {isHistory && statusLabel ? (
            <div className="flex items-center gap-1 px-1 text-body-12 text-primary-40">
              <span>{statusLabel}</span>
              {statusIcon && (
                <Icon name={statusIcon.icon} className={cn('text-[14px]', statusIcon.color)} />
              )}
            </div>
          ) : (
            hasSubtitle &&
            !isHistory && (
              <div className="flex items-start gap-1 px-1 text-body-12">
                {jobTitle && <span className="shrink-0 text-primary-100">{jobTitle}</span>}
                {jobTitle && companyName && <span className="shrink-0 text-primary-40">→</span>}
                {companyName && (
                  <span className="min-w-0 truncate text-primary-100">{companyName}</span>
                )}
              </div>
            )
          )}
          {!isHistory && !hasSubtitle && !contact.name && (
            <div className="px-1 text-body-12 text-primary-100">Job &amp; company unknown</div>
          )}
        </div>
        {showTags && trailing && <div className="shrink-0">{trailing}</div>}
      </div>
    </div>
  );
};

const Divider = () => <div className="h-px shrink-0 bg-primary-5" />;

// ─── Sticky section header ──────────────────────────────────

type SectionProps = {
  title: string;
  count: string | number;
  children: React.ReactNode;
};

const StickySection = ({ title, count, children }: SectionProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry?.isIntersecting), {
      threshold: 1,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative flex flex-col gap-2">
      <div ref={sentinelRef} className="pointer-events-none absolute top-0 h-px w-full" />
      <div className={cn('sticky top-0 z-10', '-mx-2 px-2')}>
        <div className="relative z-[2] flex items-center justify-between bg-primary-3 pb-2 px-1.5">
          <span className="text-body-12 !font-medium text-primary-100">{title}</span>
          <span className="text-body-12 text-primary-60">{count}</span>
        </div>
        {isStuck && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-6 translate-y-full">
            {[1, 2, 3, 4].map((level) => (
              <div
                key={level}
                className="absolute inset-x-0 top-0 h-full"
                style={{
                  backdropFilter: `blur(${level}px)`,
                  WebkitBackdropFilter: `blur(${level}px)`,
                  maskImage: `linear-gradient(to bottom, black ${(level - 1) * 25}%, transparent ${level * 25}%)`,
                  WebkitMaskImage: `linear-gradient(to bottom, black ${(level - 1) * 25}%, transparent ${level * 25}%)`,
                }}
              />
            ))}
            <div
              className="absolute inset-x-0 top-0 h-full"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(247,250,249,0.85) 0%, rgba(247,250,249,0.5) 40%, rgba(247,250,249,0) 100%)',
              }}
            />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
};

// ─── Main panel ─────────────────────────────────────────────

export const PipSessionPanel = ({
  session,
  onEndSession,
  windowWidth,
  onToggleList,
}: PipSessionPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const showTags = windowWidth >= 300;

  const historyContacts = session.contacts.filter(
    (c) => !['pending', 'ringing'].includes(c.dialingState) && c.id !== session.activeContactId,
  );

  const queueContacts = session.contacts.filter(
    (c) => ['pending', 'ringing'].includes(c.dialingState) || c.id === session.activeContactId,
  );

  const activeIndex = session.activeContactId
    ? queueContacts.findIndex((c) => c.id === session.activeContactId)
    : -1;
  const upNextIndex = activeIndex >= 0 ? activeIndex + 1 : 0;

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden rounded-[12px] bg-primary-3"
      style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
    >
      {/* Fixed top area — toggle + actions */}
      <div className="shrink-0">
        {/* Toggle */}
        <button
          type="button"
          onClick={() => {
            const next = !isExpanded;
            setIsExpanded(next);
            onToggleList?.(next);
          }}
          className="flex w-full cursor-pointer items-center justify-center gap-2 pt-2 pb-1"
        >
          <span className="text-body-12 !font-medium text-primary-100">
            {isExpanded ? 'Hide list' : 'Show list'}
          </span>
          <Icon
            name={Icons.ChevronDown}
            className={cn(
              'text-[12px] text-primary-100 transition-transform duration-200',
              isExpanded && 'rotate-180',
            )}
          />
        </button>

        {/* Actions — always visible */}
        <div className="flex flex-col items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={onEndSession}
            className="flex h-8 w-full cursor-pointer items-center justify-center gap-1 rounded-lg border border-white/40 bg-status-warning text-body-13 !font-medium text-white transition-opacity hover:opacity-90"
          >
            <Icon name={Icons.Stop} className="text-[16px]" />
            End session
          </button>
        </div>
      </div>

      {/* Contact list — animated collapse, scrolls independently */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-2 pb-2">
              {historyContacts.length > 0 && (
                <StickySection title="History" count={historyContacts.length}>
                  {historyContacts.flatMap((contact, index) => {
                    const slotKey = `${contact.id}-${index}`;
                    const row = (
                      <PipContactRow
                        key={slotKey}
                        contact={contact}
                        isActive={false}
                        dimmed
                        showTags={showTags}
                      />
                    );
                    if (index === 0) {
                      return [row];
                    }
                    return [<Divider key={`div-${slotKey}`} />, row];
                  })}
                </StickySection>
              )}

              {queueContacts.length > 0 && (
                <StickySection
                  title="Next in queue"
                  count={`${session.completedCount} of ${session.totalCount}`}
                >
                  {queueContacts.flatMap((contact, index) => {
                    const isActiveContact = contact.id === session.activeContactId;
                    const slotKey = `${contact.id}-${index}`;

                    const row = (
                      <PipContactRow
                        key={slotKey}
                        contact={contact}
                        isActive={isActiveContact}
                        showTags={showTags}
                        trailing={
                          isActiveContact ? (
                            <Icon
                              name={Icons.Waveform}
                              className="text-[16px] text-extra-highlight"
                            />
                          ) : index === upNextIndex ? (
                            <span className="text-body-12 !font-medium text-extra-highlight">
                              Up next
                            </span>
                          ) : undefined
                        }
                      />
                    );
                    if (index === 0) {
                      return [row];
                    }
                    return [<Divider key={`div-${slotKey}`} />, row];
                  })}
                </StickySection>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
